import { DateTime } from 'luxon';
import { isNumber, sortPartialDate } from 'neuro-utils';
import { filterDocumentsByType, sortDocumentsDescending } from 'Utility/documentHandling';

const TASK_DATE_FORMAT = 'yyyy-MM-dd HH:mm:ss' as const;

export const getLatestContraIndicationsToTreatmentDoc = (
  docs: readonly IDocumentV2[],
): IDocumentWithTypeV2<'contraIndicationsToTreatment'> | undefined => {
  return filterDocumentsByType(docs as IDocumentV2[], ['contraIndicationsToTreatment'])
    .sort(sortDocumentsDescending)
    .at(0);
};

export const getTDCSAndRTMSDocs = (docs: readonly IDocumentV2[]): Array<IDocumentWithTypeV2<'rtms' | 'tdcs'>> => {
  return filterDocumentsByType(docs as IDocumentV2[], ['rtms', 'tdcs']).sort((a, b) => sortPartialDate(b.date, a.date));
};

const recurringTasksAllowedByPlatform: { [platform in Platform]?: boolean } = {
  ninmt: true,
};

export const shouldAllowRecurringTasks = ({
  platform,
  tasklist,
}: {
  platform: Platform | undefined;
  tasklist: Partial<ITaskList>;
}): boolean => {
  if (!platform) return false;
  if (!tasklist.template_id) return false;
  if (tasklist.appointment_id) return false;
  return !!recurringTasksAllowedByPlatform[platform];
};

export const parseSendDate = ({
  tasklist,
  templateSettings,
}: {
  tasklist: Readonly<Partial<ITaskList>>;
  templateSettings: Readonly<TaskStimulusFormSettings>;
}): PartialDate | undefined => {
  const { template_id, recurrence, tasks } = tasklist;
  let { end_exclusive } = tasklist;
  if (recurrence?.recurring) end_exclusive = recurrence.first_deadline;
  if (!end_exclusive) return undefined;
  if (!template_id) return undefined;
  let { sendDateDaysBeforeDeadline } = templateSettings[template_id] ?? {};
  // idk where this should come from TODO; if 'ninmtPreInquiry' found, set sendDate to end - 2 weeks
  if (tasks?.some(t => t === 'ninmtPreInquiry')) sendDateDaysBeforeDeadline = 14;
  if (!isNumber(sendDateDaysBeforeDeadline)) return undefined;
  if (sendDateDaysBeforeDeadline < 0) return undefined;
  let sendDate = DateTime.fromFormat(end_exclusive, TASK_DATE_FORMAT).minus({
    milliseconds: 1,
    days: sendDateDaysBeforeDeadline,
  });
  if (!sendDate.isValid) return undefined;
  // if earlier than now, set now as sendDate
  if (sendDate < DateTime.now()) sendDate = DateTime.now();
  return [sendDate.year, sendDate.month, sendDate.day];
};

export const parseRecurringTaskDeadline = ({
  tasklist,
  templateSettings,
}: {
  tasklist: Readonly<Partial<ITaskList>>;
  templateSettings: Readonly<TaskStimulusFormSettings>;
}): ITaskList['end_exclusive'] | undefined => {
  const { template_id, recurrence } = tasklist;
  if (!template_id) return undefined;
  if (!recurrence?.recurring) return undefined;
  const { first_deadline } = recurrence;
  if (!first_deadline) return undefined;
  const { recurringTaskDeadlineDefaultDays } = templateSettings[template_id] ?? {};
  if (!isNumber(recurringTaskDeadlineDefaultDays)) return undefined;
  if (recurringTaskDeadlineDefaultDays < 0) return undefined;
  const end_exclusive = DateTime.fromFormat(first_deadline, TASK_DATE_FORMAT).plus({
    days: recurringTaskDeadlineDefaultDays,
  });
  if (!end_exclusive.isValid) return undefined;
  return end_exclusive.toFormat(TASK_DATE_FORMAT);
};
